import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/layout/layout";
import { CssBaseline, Grid } from "@mui/material";
import LoginPage from "./login";
import { BASE_PATH, Configuration, Login, PlayerApi } from "./reckon-api";
import ReferentialContext, { targets } from "./Context";
import Box from "@mui/material/Box";
import GameReviewer from "./pages/scouting/CheckGames";
import ManageOrg from "./pages/Management/ManageOrgs";
import PositionHeatmap from "./pages/scouting/PositionHeatmap";
import RoundStats from "./pages/Competitive/RoundStats";
import { useThemeContext } from "./themeContext";
import CompetitivePlayerStats from "./pages/Competitive/PlayerStats";
import CompetitiveMapStats from "./pages/Competitive/MapStats";
import CompetitiveTeamDrafts from "./pages/Competitive/Drafts";
import KillsHeatmap from "./pages/Competitive/KillsHeatmap";
import ProfilePage from "./pages/Profile/ProfilePage";
import RiotCallback from "./pages/Profile/RiotCallback";
import TermsOfService from "./pages/TermsOfService";

function useWindowSize() {
    const [windowSize, setWindowSize] = useState({
        width: undefined as number | undefined,
        height: undefined as number | undefined,
    });
    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return windowSize;
}

export default function App() {
    const size = useWindowSize();
    const location = useLocation();

    const stored_user = localStorage.getItem("reckonBoltUser");

    const [user, setUser] = useState(stored_user ? JSON.parse(stored_user) : (null as Login | null));
    const [userValidated, setUserValidated] = useState(true);

    // Public routes accessible without authentication
    if (location.pathname === "/terms-of-service") {
        return (
            <Box
                sx={{
                    width: "100%",
                    minHeight: "100vh",
                    background: "linear-gradient(180deg, #0a0f18 0%, #05080d 100%)",
                    overflow: "auto",
                }}
            >
                <TermsOfService />
            </Box>
        );
    }

    if (user) {
        const api_config = new Configuration({
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${user.token}`,
            },
            basePath: BASE_PATH,
        });
        const api = new PlayerApi(api_config);
        api.get({ id: "7ssk7" })
            .then((p: any) => setUserValidated(true))
            .catch((e: any) => {
                setUserValidated(false);
                setUser(null);
            });
    }

    function log_user_in(user: any) {
        setUser(user);
        setUserValidated(true);
    }

    function log_user_out() {
        setUser(null);
        setUserValidated(false);
        localStorage.removeItem("reckonBoltUser");
    }

    document.title = "Reckon Bolt";
    if (!user?.token || !userValidated) {
        return <LoginPage setToken={log_user_in} />;
    }

    return (
        <ReferentialContext user={user}>
            <Box sx={{ width: "100%", height: "100%" }}>
                <AppPages logout={log_user_out} />
            </Box>
        </ReferentialContext>
    );
}

function AppPages(props: { logout: () => void }) {
    const theme = useThemeContext();

    return (
        <Grid container sx={{ width: "100%", height: "100%" }}>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    "&::before": {
                        content: '""',
                        display: "block",
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        background: "black",
                        filter: theme.mode === "dark" ? "brightness(0.2)" : "brightness(1)",
                        zIndex: -1,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center center",
                    },
                    "& .neon-glow": {
                        position: "absolute",
                        opacity: 0.05,
                        borderRadius: "50%",
                        pointerEvents: "none",
                        zIndex: 0,
                        width: "10%",
                        height: "20%",
                    },
                }}
            >
                <CssBaseline />
                <div
                    className="neon-glow"
                    style={{
                        top: "10%",
                        left: "0%",
                        width: "30%",
                        height: "50%",
                        background: "#fff",
                        filter: "blur(50px)",
                        animation: "pulsate_background_blur 3s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "10%",
                        left: "60%",
                        width: "30%",
                        height: "30%",
                        background: "#fff",
                        filter: "blur(50px)",
                        animation: "pulsate_background_blur 3s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "0%",
                        left: "30%",
                        width: "30%",
                        height: "30%",
                        background: "#fff",
                        filter: "blur(50px)",
                        animation: "pulsate_background_blur 3s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "15%",
                        left: "15%",
                        width: "30%",
                        height: "50%",
                        background: "#ba1cbe",
                        filter: "blur(25px)",
                        animation: "pulsate_background_blur 4s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "20%",
                        left: "30%",
                        width: "30%",
                        height: "50%",
                        background: "#3ba9e1",
                        filter: "blur(35px)",
                        animation: "pulsate_background_blur 5s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "25%",
                        left: "45%",
                        width: "30%",
                        height: "50%",
                        background: "#d4bede",
                        filter: "blur(45px)",
                        animation: "pulsate_background_blur 4s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "30%",
                        left: "60%",
                        width: "30%",
                        height: "50%",
                        background: "#3091f6",
                        filter: "blur(25px)",
                        animation: "pulsate_background_blur 3s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "60%",
                        left: "0%",
                        width: "30%",
                        height: "30%",
                        background: "#fff",
                        filter: "blur(50px)",
                        animation: "pulsate_background_blur 3s infinite alternate",
                    }}
                />
                <div
                    className="neon-glow"
                    style={{
                        top: "55%",
                        left: "60%",
                        width: "30%",
                        height: "30%",
                        background: "#fff",
                        filter: "blur(50px)",
                        animation: "pulsate_background_blur 3s infinite alternate",
                    }}
                />
                <Layout logout={props.logout} />
                <Box
                    sx={{
                        display: "flex",
                        height: "92%",
                        overflow: "auto",
                        zIndex: 10,
                        "&::-webkit-scrollbar": {
                            width: "0.2em",
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "#e1e1e1e",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#888",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: "#555",
                        },
                    }}
                >
                    <Box
                        sx={{
                            filter: "brightness(1)",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <Routes>
                            {/* Default redirect */}
                            <Route path="/" element={<Navigate to="/competitive-game-review" replace />} />

                            {/* Competitive routes */}
                            <Route path="/competitive-game-review" element={<GameReviewer target={targets.competitive} />} />
                            <Route path="/competitive-kills-heatmap" element={<KillsHeatmap target={targets.competitive} />} />
                            <Route path="/competitive-smokes-heatmap" element={<></>} />
                            <Route path="/competitive-positions-heatmap" element={<PositionHeatmap target={targets.competitive} />} />
                            <Route path="/competitive-clutches" element={<></>} />
                            <Route path="/competitive-rounds" element={<RoundStats target={targets.competitive} />} />
                            <Route path="/competitive-players" element={<CompetitivePlayerStats />} />
                            <Route path="/competitive-maps" element={<CompetitiveMapStats />} />
                            <Route path="/competitive-drafts" element={<CompetitiveTeamDrafts />} />

                            {/* Profile routes */}
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/riot-callback" element={<RiotCallback />} />

                            {/* Management routes */}
                            <Route path="/management-organizations" element={<ManageOrg />} />

                            {/* Legal */}
                            <Route path="/terms-of-service" element={<TermsOfService />} />

                            {/* Catch-all redirect */}
                            <Route path="*" element={<Navigate to="/competitive-game-review" replace />} />
                        </Routes>
                    </Box>
                </Box>
            </Box>
        </Grid>
    );
}
